import React from 'react';

import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';

import { formatNumber } from 'utils/formatting';
import { getTenantSetting } from 'utils/tenantSettings';

import { calcLine, calcOp, calcProp, Equation, noteLine } from './Equation';
import { EquationPopover } from './EquationPopover';

export interface RequiredQtyRecord {
  requiredQty: number;
  destinationMax: number;
  destinationMin: number;
  destinationNetStock: number;
  destinationBackOrderQty: number;
  replenishmentMethod: string;
  stockable: boolean;
  unitOfMeasure: string;
}

/**
 * Estimate how much the downstream spokes require based off of the required qty and available amount.
 */
export function getSpokeRequiredQty(record: RequiredQtyRecord): number {
  if (record.replenishmentMethod === 'OP/OQ' || !record.stockable || record.requiredQty <= 0) {
    return 0;
  }
  return record.requiredQty - (record.destinationMax - record.destinationNetStock);
}

export function RequiredQtyPopover({ record }: { record: RequiredQtyRecord }) {
  const showOpOq = record.replenishmentMethod === 'OP/OQ';
  const isNonStock = !record.stockable;
  const { destinationMin: orderPoint, destinationMax: orderQuantity } = record;
  const orderCount = Math.ceil((orderPoint - record.destinationNetStock) / orderQuantity);
  const includeDownstreamNeeds = getTenantSetting(TenantSettingKey.FeatureTransferReplenishmentIncludeDownstreamNeeds);
  const spokeRequiredQty = includeDownstreamNeeds ? getSpokeRequiredQty(record) : 0;
  // with downstream needs on, Min/Max destinations fill to max, so the min is no longer why a line is zero
  const coversMax =
    includeDownstreamNeeds && !showOpOq && !isNonStock && record.destinationNetStock >= record.destinationMax;
  return (
    <EquationPopover title="Required Quantity Calculation">
      <Equation
        lines={
          isNonStock && record.destinationNetStock >= 0
            ? [
                calcLine(
                  calcProp('Net Stock Quantity', `${formatNumber(record.destinationNetStock)} ${record.unitOfMeasure}`),
                  calcOp('>='),
                  calcProp('Minimum Quantity', `0 ${record.unitOfMeasure}`),
                ),
                noteLine(
                  'No inventory needs to be transferred as the current net stock is greater than or equal to 0.',
                ),
              ]
            : record.destinationNetStock >= record.destinationMin && record.requiredQty <= 0
            ? [
                calcLine(
                  calcProp('Net Stock Quantity', `${formatNumber(record.destinationNetStock)} ${record.unitOfMeasure}`),
                  calcOp('>='),
                  coversMax
                    ? calcProp('Current Maximum', `${formatNumber(record.destinationMax)} ${record.unitOfMeasure}`)
                    : calcProp('Current Minimum', `${formatNumber(record.destinationMin)} ${record.unitOfMeasure}`),
                ),
                noteLine(
                  coversMax
                    ? 'No inventory needs to be transferred as the current net stock covers the Current Maximum and what the locations it replenishes need.'
                    : 'No inventory needs to be transferred as the current net stock is greater than or equal to the Current Minimum.',
                ),
              ]
            : [
                calcLine(
                  calcProp('Required Quantity', `${formatNumber(record.requiredQty)} ${record.unitOfMeasure}`),
                  calcOp('='),
                  ...(isNonStock && record.destinationNetStock < 0
                    ? [
                        calcOp('-'),
                        calcProp(
                          'Net Stock Quantity',
                          `${formatNumber(record.destinationNetStock)} ${record.unitOfMeasure}`,
                        ),
                      ]
                    : showOpOq
                    ? [
                        calcProp('Number of Orders', `${formatNumber(orderCount)} ${record.unitOfMeasure}`),
                        calcOp('x'),
                        calcProp('Order Quantity', `${formatNumber(orderQuantity)} ${record.unitOfMeasure}`),
                      ]
                    : [
                        calcProp('Destination Max', `${formatNumber(record.destinationMax)} ${record.unitOfMeasure}`),
                        calcOp('-'),
                        calcProp(
                          'Destination Net Stock',
                          `${formatNumber(record.destinationNetStock)} ${record.unitOfMeasure}`,
                        ),
                        ...(spokeRequiredQty > 0
                          ? [
                              calcOp('+'),
                              calcProp(
                                'Spoke Requirements',
                                `${formatNumber(spokeRequiredQty)} ${record.unitOfMeasure}`,
                              ),
                            ]
                          : []),
                      ]),
                ),
                spokeRequiredQty > 0
                  ? noteLine(
                      'Includes what the locations this destination replenishes will request from it, so one transfer covers the chain below it.',
                    )
                  : null,
              ]
        }
      />
    </EquationPopover>
  );
}
